import React, { useContext, useEffect, useState } from 'react';
import { Text } from '@chakra-ui/react';
import { PluginProps } from '../Plugin';

import axios from 'axios';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { fullNameUsernameOrEmail } from '../../../../../helpers/username';
import CloudinaryUploadWidget from '../../../../app/components/CloudinaryUploadWidget';
import getStudents from '../../../helpers/getStudents';
import { DASHBOARD_STUDYGROUPS_AND_PINNIGS } from '../../../../app/pages/queries';
import { useQuery } from '@apollo/client';
import { Query, StudyGroup } from '../../../../../generated/graphqlModels';
import useTeacherStudyGroup from '../../../hooks/useTeacherStudyGroup';
import useFileUploadsFromActiveLicenses from '../../../hooks/useFileUploadsFromActiveLicenses';
import AdminFileUploadsList from './AdminFileUploadsList';

// used for challenges only as they dont belong to any study group and we need to send something to backend to get/save files
export const CHALLENGE_VIRTUAL_STUDY_GROUP_ID = '9ec75b18-0396-4b2f-ae26-1cd94aa770a6';

const FileBlock = (props: PluginProps) => {
  const user = useContext(AuthContext);
  const { content, studyGroup } = props;
  const {
    teacherStudyGroups,
    students: studentsFromTeacherStudyGroups,
  } = useTeacherStudyGroup();

  const contentData = content.data;
  const currentFileSlug = contentData.slug;
  const fileUploadType = contentData.type;

  const [students, setStudents] = useState<any[]>([]);
  const [areStudentsLoaded, setAreStudentsLoaded] = useState(false);
  const [initialData, setInitialData] = useState<any>({});
  const [allInitialData, setAllInitialData] = useState<any[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [isSlugLoading, setIsSlugLoading] = useState<Record<string, boolean>>({});

  const isTeacher =
    user?.roles.includes(Role.teacher) ||
    user?.roles.includes(Role.manager) ||
    user?.roles.includes(Role.organization_user);

  const isStaff = user?.roles.includes(Role.staff);

  useEffect(() => {
    if (isStaff) {
      // we handle staff different in the AdminFileUploadsList
      return;
    }

    if (isTeacher) {
      if (studyGroup?.pk) {
        getStudents(studyGroup.pk).then((students) => {
          setStudents(
            students.filter(
              (s: any) => fullNameUsernameOrEmail(s) !== '[deleted]',
            ),
          );
          setAreStudentsLoaded(true);
        });
      } else if (studentsFromTeacherStudyGroups) {
        setStudents(studentsFromTeacherStudyGroups);
        setAreStudentsLoaded(true);
      }
    }

    if (studyGroup || teacherStudyGroups?.length) {
      getOldFiles();
    }
  }, [studyGroup, teacherStudyGroups, studentsFromTeacherStudyGroups, isTeacher]);

  const saveFile = (slug: string, url: string, filename: string) => {
    isSlugLoading[slug] = true;
    setIsSlugLoading({ ...isSlugLoading });
    axios
      .post(
        '/api/v1/syllabus/user_file_upload/',
        {
          slug,
          class_id: studyGroup?.pk || CHALLENGE_VIRTUAL_STUDY_GROUP_ID,
          file_url: url,
          filename,
        },
        {
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        },
      )
      .then((response) => { })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        isSlugLoading[slug] = false;
        setIsSlugLoading({ ...isSlugLoading });
      });
  };

  const deleteFile = () => {
    axios
      .delete('/api/v1/syllabus/user_file_upload/', {
        params: {
          class_id: studyGroup?.pk || CHALLENGE_VIRTUAL_STUDY_GROUP_ID,
          slug: currentFileSlug,
        },
        headers: {
          'X-CSRFToken': JSON.parse(
            document.getElementById('csrf_token')!.innerHTML,
          ),
        },
      })
      .then((response) => {
        // eslint-disable-next-line no-restricted-globals
        location.reload();
      });
  };

  const getOldFiles = () => {
    axios
      .get('/api/v1/syllabus/user_file_upload/', {
        params: {
          class_id: studyGroup?.pk || CHALLENGE_VIRTUAL_STUDY_GROUP_ID,
          slug: currentFileSlug,
        },
        headers: {
          'X-CSRFToken': JSON.parse(
            document.getElementById('csrf_token')!.innerHTML,
          ),
        },
      })
      .then((response) => {
        let initialUrl = null;
        let initialFilename = null;
        let data: any = [];
        try {
          data = JSON.parse(response.data);
          if (data.length) {
            initialUrl = data[0].fields.file_url;
            initialFilename = data[0].fields.filename;
            setAllInitialData(data);
            setInitialData({ initialUrl, initialFilename });
          }
        } catch (e) {
          console.log(e);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <div className="file-block">
      <Text size="xl">{contentData.title}</Text>
      {!isStaff && <div>
        {isTeacher ? (
          <div>
            {students.length === 0 && <Text fontSize="sm">No students</Text>}
            {students.map((s: any, index: number) => {
              const d = allInitialData.find(
                ({ fields }: any) => fields.student_id === s.pk,
              );
              const studentName = fullNameUsernameOrEmail(s);
              return (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    margin: '4px',
                  }}
                >
                  <span style={{ width: '260px', maxWidth: '260px' }}>
                    {studentName}
                  </span>
                  <CloudinaryUploadWidget
                    isViewOnly={true}
                    isImageOnly={fileUploadType === 'image'}
                    initialUrl={d?.fields?.file_url || null}
                    initialFilename={d?.fields?.filename || null}
                    onImageUpload={({
                      url,
                      original_filename,
                      resource_type,
                    }) => {
                      urls[currentFileSlug] = url;
                      setUrls({ ...urls });
                      saveFile(currentFileSlug, url, original_filename);
                    }}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            <CloudinaryUploadWidget
              isImageOnly={fileUploadType === 'image'}
              initialUrl={initialData.initialUrl}
              initialFilename={initialData.initialFilename}
              onImageUpload={({ url, original_filename, resource_type }) => {
                urls[currentFileSlug] = url;
                setUrls({ ...urls });
                saveFile(currentFileSlug, url, original_filename);
              }}
              onDelete={() => {
                deleteFile();
              }}
            />
          </div>
        )}
      </div>}
      {isStaff && <AdminFileUploadsList slug={currentFileSlug} />}
    </div>
  );
};

export default FileBlock;
